import { InfoOutlined } from '@mui/icons-material';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Tooltip } from '@mui/material';
import { action, computed, extendObservable, makeObservable, observable, runInAction } from 'mobx';
import { Observer, observer } from 'mobx-react';
import React from 'react';
import AutoComplete from '../misc/AutoComplete';
import { IModelo, TipoMinuta } from '../modelo';
import MinutaService from '../services/MinutaService';
import modeloDocumentoService from '../services/ModeloDocumentoService';
import { UsuarioStore } from '../store';
import EscaninhoStore from '../store/EscaninhoStore';
import MinutaUtils from '../utils/MinutaUtils';

interface IPropsListaModeloMinuta {
    tipo: number;
    atualizaModelo?: (conteudo, nome, categoria, codigo, incluirAposPauta?: boolean) => any;
    botaoAplicar?: boolean;
    incluirAposPauta?: boolean;
}


class ListaModeloMinutas extends React.Component<IPropsListaModeloMinuta> {

    tipo = this.props.tipo;
    incluirAposPautaSelecionado = !!this.props.incluirAposPauta;
    modelos = [];
    nomesModelos = [];
    nomeModeloFiltro = '';
    modeloSelecionado: IModelo = undefined;
    openErro = false;
    carregandoModelos = false;
    modeloDocumentoService = modeloDocumentoService;

    constructor(props: IPropsListaModeloMinuta) {
        super(props);
        makeObservable(this, {
            tipo: observable,
            incluirAposPautaSelecionado: observable,
            modelos: observable,
            nomesModelos: observable,
            nomeModeloFiltro: observable,
            modeloSelecionado: observable,
            openErro: observable,
            carregandoModelos: observable,
            idProcesso: computed,
            conteudoHtmlSelecionado: computed,
            handleFiltroNomeModelo: action,
            componentDidMount: action,
            handleCloseDialog: action,
        });
    }

    get idProcesso() {
        // TODO: conferir isto aqui
        return EscaninhoStore.processosSelecionados[0].identificadorProcesso;
    }

    get conteudoHtmlSelecionado() {
        if (!this.modeloSelecionado) {
            return '';
        }

        const conteudoHtml = this.modeloSelecionado.conteudoHtml || '';
        return this.aplicarAposPautaNoPreview(conteudoHtml);
    }

    aplicarAposPautaNoPreview(conteudoHtml: string) {
        if (!conteudoHtml) {
            return conteudoHtml;
        }

        const tipoEhDespachoOuDecisao = this.tipo === TipoMinuta.DESPACHO || this.tipo === TipoMinuta.DECISAO;
        if (!tipoEhDespachoOuDecisao || !this.incluirAposPautaSelecionado) {
            return conteudoHtml;
        }

        const textoAlvo = 'Após, à Pauta.';
        const normalizar = (texto: string) => (texto || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .replace(/[.,;:!?()"']/g, '')
            .replace(/\s+/g, ' ')
            .trim();

        const html = document.createElement('div');
        html.innerHTML = conteudoHtml;

        let elementosTexto = Array.from(html.querySelectorAll('p, div, span')) as HTMLElement[];

        const elementosAposPauta = elementosTexto.filter((el) => {
            const texto = normalizar(el.textContent || '');
            return texto === 'apos a pauta' || /\bapos\s*,?\s*a\s+pauta\b|\bapos\s*,?\s+pauta\b/.test(texto);
        });

        elementosAposPauta.forEach((el) => {
            el.parentNode?.removeChild(el);
        });

        elementosTexto = Array.from(html.querySelectorAll('p, div, span')) as HTMLElement[];

        const candidatosPubliquese = elementosTexto
            .map((el) => ({
                el,
                texto: normalizar(el.textContent || '')
            }))
            .filter((item) => (
                item.texto.length > 0
                && item.texto.length <= 80
                && (item.texto === 'publique-se' || item.texto === 'publiquese' || /\bpublique\s*-?\s*se\b/.test(item.texto))
            ))
            .sort((a, b) => a.texto.length - b.texto.length);

        const alvoPubliquese = candidatosPubliquese[0]?.el;

        const assinatura = elementosTexto.find((el) => el.getAttribute('data-sv-topico') === 'assinatura');
        const tagsDeBloco = ['P', 'DIV', 'LI'];

        const aplicarTextoMantendoEstrutura = (elemento: HTMLElement, novoTexto: string) => {
            const regexPubliquese = /publique\s*-?\s*se\.?/i;
            const walker = document.createTreeWalker(elemento, NodeFilter.SHOW_TEXT);

            let noAtual = walker.nextNode() as Text | null;
            while (noAtual) {
                const atual = noAtual.nodeValue || '';
                if (regexPubliquese.test(atual)) {
                    noAtual.nodeValue = atual.replace(regexPubliquese, novoTexto);
                    return;
                }
                noAtual = walker.nextNode() as Text | null;
            }

            elemento.textContent = novoTexto;
        };

        const obterBlocoDaLinha = (elemento: HTMLElement): HTMLElement => {
            let atual: HTMLElement | null = elemento;
            while (atual && atual !== html) {
                if (tagsDeBloco.includes(atual.tagName)) {
                    return atual;
                }
                atual = atual.parentElement;
            }
            return elemento;
        };

        let novoParagrafo: HTMLElement;
        let ancoraInsercao: HTMLElement | undefined;
        if (alvoPubliquese) {
            ancoraInsercao = obterBlocoDaLinha(alvoPubliquese);
            novoParagrafo = ancoraInsercao.cloneNode(true) as HTMLElement;
            aplicarTextoMantendoEstrutura(novoParagrafo, textoAlvo);
        } else {
            novoParagrafo = document.createElement('p');
            novoParagrafo.setAttribute('data-sv-topico', 'corpo');
            novoParagrafo.setAttribute('class', 'corpo');
            novoParagrafo.textContent = textoAlvo;
        }

        if (ancoraInsercao?.parentNode) {
            ancoraInsercao.parentNode.insertBefore(novoParagrafo, ancoraInsercao.nextSibling);
        } else if (assinatura?.parentNode) {
            assinatura.parentNode.insertBefore(novoParagrafo, assinatura);
        } else {
            html.appendChild(novoParagrafo);
        }

        return html.innerHTML;
    }

    carregaModelos = async (tipo) => {
        const modelos = [];
        const modeloEmBranco = {
            nome: "Documento em branco",
            conteudo: ""
        };
        modelos.push(modeloEmBranco);
        modelos.push({ nome: "Modelo Padrão", codigo: 0});
        const modelosMinuta = await this.modeloDocumentoService.modelosDoGabinetePorTipo(UsuarioStore.gabinete, tipo);
        modelosMinuta.sort((m1, m2) => {
            if (m1.nome < m2.nome) {
                return -1;
            }
            if (m1.nome > m2.nome) {
                return 1;
            }
            return 0;
        });
        modelosMinuta.map((modelo) => {
            modelos.push({
                codigo: modelo.codigo,
                nome: modelo.nome,
                categorias: modelo.categoria,
                incluirAposPauta: !!modelo.incluirAposPauta,
            });
        });
        return modelos;
    }

    async selecionarModelo(valor) {
        const modelo = valor.valor;
        if (!modelo.conteudo && modelo.conteudo !== "") {
            let conteudoModelo;
            if (modelo.nome === "Modelo Padrão") {
                conteudoModelo = await this.modeloDocumentoService.conteudoModeloPadrao(this.tipo);
            } else {
                conteudoModelo = await this.modeloDocumentoService.conteudoModelo(UsuarioStore.gabinete, this.tipo, modelo.codigo, "Base64");
                if (conteudoModelo && MinutaUtils.decodeMinuta(conteudoModelo).indexOf('<div ') > -1) {
                    runInAction(() => {
                        this.openErro = true;
                    })
                    return;
                }
            }
            const conteudo = MinutaUtils.decodeMinuta(conteudoModelo);
            extendObservable(modelo, {
                conteudo: conteudoModelo,
                conteudoHtml: conteudo
            });
            modelo.tipoMinuta = this.tipo;
            modelo.codigoModelo = modelo.codigo;
            if (conteudo.indexOf('<div ') < 0) {
                const html = await MinutaService.converter64toHtml(modelo.conteudo, true);
                runInAction(() => {
                    modelo.conteudoHtml = html;
                });
            }
            this.modelos.map(mod => {
                if (mod.codigo === modelo.codigo) {
                    mod.codigo = modelo.codigo;
                }
                return mod;
            });
        }
        runInAction(() => {
            this.modeloSelecionado = modelo;
        });

        this.props.atualizaModelo(modelo.conteudo, modelo.nome, modelo.categorias, modelo.codigo, !!modelo.incluirAposPauta);
    }

    handleFiltroNomeModelo = (ev, value) => {
        if (value) {
            this.nomeModeloFiltro = value.texto;
            this.selecionarModelo(value);
        } else {
            this.nomeModeloFiltro = '';
        }
    }

    componentDidMount() {
        // Precisa ser síncrono (não pode retornar Promise): em React 19 + mobx-react 9,
        // um componentDidMount `async` quebra permanentemente a reatividade do observer().
        (async () => {
            runInAction(() => {
                this.carregandoModelos = true;
            });
            try {
                const modelos = await this.carregaModelos(this.props.tipo);
                const nomes = [];
                modelos.map(modelo => {
                    nomes.push({
                        texto: modelo.nome,
                        valor: modelo,
                        parametrosBusca: [modelo.nome]
                    });
                });
                runInAction(() => {
                    this.nomesModelos = nomes;
                    this.modelos = modelos;
                });
            } catch (e) {
                console.error('[ListaModeloMinutas] Falha ao carregar modelos:', e);
                runInAction(() => {
                    this.nomesModelos = [];
                    this.modelos = [];
                });
            } finally {
                runInAction(() => {
                    this.carregandoModelos = false;
                });
            }
        })();
    }

    componentDidUpdate(prevProps: Readonly<IPropsListaModeloMinuta>) {
        if (prevProps.incluirAposPauta !== this.props.incluirAposPauta) {
            runInAction(() => {
                this.incluirAposPautaSelecionado = !!this.props.incluirAposPauta;
            });
        }
    }

    handleCloseDialog = () => {
        this.openErro = false;
    }

    render() {
        return (
            <div style={{ width: '100%' }}>
                <div style={{ display: 'flex', width: '100%' }}>
                    <div style={{ display: 'flex', alignSelf: 'center', width: '100%' }}>
                        <Observer>
                            {() => (
                                <AutoComplete
                                    callback={this.handleFiltroNomeModelo}
                                    label='Nome do modelo'
                                    options={this.nomesModelos}
                                    id='FILTRO_MODELO'
                                    apresentarTextoSecundario
                                    loading={this.carregandoModelos}
                                />
                            )}
                        </Observer>
                    </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', marginRight: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <Tooltip title='O rodapé dos documentos é reservado para a assinatura, portanto, textos colocados nesse espaço não serão salvos.'>
                            <InfoOutlined style={{ color: '#757575', fontSize: '18px' }} />
                        </Tooltip>
                        <h4>Visualização do modelo: </h4>
                    </div>
                    <Paper elevation={2} style={{ overflowY: 'auto', height: 450, marginTop: '4px' }}>
                        <Paper elevation={0}
                            style={{
                                cursor: 'pointer',
                                paddingBottom: '20px', display: 'flex',
                                justifyContent: 'center', alignContent: 'center', alignItems: 'center'
                            }}>
                            <Observer>
                                {() => (
                                    <div dangerouslySetInnerHTML={{ __html: this.conteudoHtmlSelecionado }} style={{
                                        paddingTop: '20px',
                                        paddingLeft: '20px', paddingRight: '20px', overflow: 'hidden'
                                    }} />
                                )}
                            </Observer>
                        </Paper>
                    </Paper>
                </div>
                <Dialog
                    open={this.openErro}
                    onClose={this.handleCloseDialog}
                >
                    <DialogTitle>
                        Erro
                    </DialogTitle>
                    <DialogContent>
                        Este modelo precisa ser convertido para .docx. Favor fazer a conversão na tela de Modelos de Minutas e tentar usa-lo novamente.
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant='contained'
                            color='primary'
                            onClick={this.handleCloseDialog}
                        >
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        );
    }
}

export default observer(ListaModeloMinutas);
